import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GomakePrimaryButton, SecondSwitch, GoMakeDeleteModal } from "@/components";
import { GoMakeDialog } from "@/components/dialog";
import { GoMakePagination } from "@/components/pagination/gomake-pagination";
import { useStyle } from "./style";
import { PrimaryTable } from "@/components/tables/primary-table";
import { DeleteMenuIcon } from "@/widgets/quote-new/more-circle/icons/delete-menu";
import { ScheduledReport } from "@/hooks/use-scheduled-reports";
import { useListPagination } from "@/hooks/use-list-pagination";
import { ModalType } from "@/enums";
import { NoDataComponent } from "@/widgets/quote-new/receipts-table/Payment-methods-details/no-data-component";

interface ScheduledReportsTableProps {
    openModal: boolean;
    onCloseModal: () => void;
    scheduledReports?: ScheduledReport[];
    totalCount?: number;
    pageNumber?: number;
    pageSize?: number;
    pagesCount?: number;
    isLoading?: boolean;
    onToggleActive?: (id: string, isActive: boolean) => void;
    onDelete?: (id: string) => void;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
}

const ScheduledReportsTable = ({
    openModal,
    onCloseModal,
    scheduledReports = [],
    totalCount = 0,
    pageNumber = 1,
    pageSize = 10,
    pagesCount = 1,
    isLoading = false,
    onToggleActive,
    onDelete,
    onPageChange,
    onPageSizeChange,
}: ScheduledReportsTableProps) => {
    const { t } = useTranslation();
    const { classes } = useStyle();
    const { initialPageSize } = useListPagination();

    // State for delete confirmation modal
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [reportToDelete, setReportToDelete] = useState<string | null>(null);

    const headers = [
        t("sales.list.reportName"),
        t("products.profits.exceptions.detailsOption"),
        t("sales.list.status"),
        t("sales.list.actions"),
    ];
    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        onPageChange?.(page);
    };

    const handlePageSizeChange = (event: any, child: React.ReactNode) => {
        onPageSizeChange?.(event.target.value as number);
    };

    // Handle delete confirmation
    const handleDeleteClick = (reportId: string) => {
        setReportToDelete(reportId);
        setOpenDeleteModal(true);
    };

    const handleConfirmDelete = () => {
        if (reportToDelete) {
            onDelete?.(reportToDelete);
            setOpenDeleteModal(false);
            setReportToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setOpenDeleteModal(false);
        setReportToDelete(null);
    };

    const rows = (scheduledReports || []).map((report: any) => [
        report.reportName,
        report.exportType === 1 ? t("products.profits.exceptions.withDetails") : report.exportType === 2 ? t("products.profits.exceptions.withoutDetails") : report.exportType === 3 ? t("sales.list.exportExtendedTableExcel") : report.exportType === 4 ? t("sales.list.exportExtendedTableTxt") : report.exportType === 5 ? t("sales.list.exportSummeryExcelWithoutVat") : t("sales.list.exportSummeryExcelWithVat"),
        <SecondSwitch
            key={`switch-${report.id}`}
            checked={report.isActive}
            onChange={(e) => onToggleActive?.(report.id, e.target.checked)}
        />,
        <div key={`actions-${report.id}`} style={{ ...classes.actionButtons, cursor: "pointer" }} onClick={() => {
            handleDeleteClick(report.id)
        }} >
            <DeleteMenuIcon />
        </div>,
    ]);

    return (
        <>
            <GoMakeDialog
                open={openModal}
                onClose={onCloseModal}
                title={t("sales.list.scheduledReports")}
                maxWidth="md"
                hidePrimaryButton
                fullWidth
                // customFooter={
                //     <div style={classes.buttonContainer}>
                //         <GomakePrimaryButton
                //             style={classes.closeButton}
                //             onClick={onCloseModal}
                //         >
                //             {t("modal.close")}
                //         </GomakePrimaryButton>
                //     </div>
                // }
                customStyles={{
                    paper: {
                        borderRadius: "4px",
                        maxHeight: "90vh",
                    },
                    content: {
                        padding: "10px",
                        backgroundColor: "#fafafa",
                    },
                }}
            >
                <div style={classes.mainContainer}>
                    <div style={{ minHeight: "432px", marginTop: "10px" }}>
                        {scheduledReports.length === 0 ? (
                            <div style={classes.noDataContainer}>
                                <NoDataComponent
                                    subNoDataText={t("sales.list.scheduledReports")}
                                />
                            </div>
                        ) : (
                            <>
                                <PrimaryTable
                                    key={`scheduled-reports-table-${pageNumber}-${scheduledReports.length}`}
                                    stickyHeader={true}
                                    maxHeight={"395px"}
                                    rows={rows}
                                    headers={headers}
                                    variant="OddEvenTable"
                                />
                                <div>
                                    <GoMakePagination
                                        onChangePageNumber={handlePageChange}
                                        onChangePageSize={handlePageSizeChange}
                                        page={pageNumber}
                                        setPage={onPageChange || (() => { })}
                                        pageSize={pageSize}
                                        pagesCount={pagesCount}
                                        initialPageSize={initialPageSize}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </GoMakeDialog>
            <GoMakeDeleteModal
                title={t("sales.list.deleteReport")}
                yesBtn={t("modal.yes")}
                modalType={ModalType.WARNING}
                openModal={openDeleteModal}
                onClose={handleCancelDelete}
                onClickDelete={handleConfirmDelete}
            />
        </>



    );
};

export { ScheduledReportsTable };
